import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
import { mdiClockFast } from "@mdi/js"
import {
  markManualLetterStepDueNow,
  type ContactOrder,
  type ContactOrderStep,
} from "../../../../../../../../api/kehrblatt/contact-orders"
import { isDueManualLetterStep } from "../_logic/letterDeliveryEligibility"
import type { ContactOrderPlanRow } from "../../../_logic/communication-plan-table-contract"
import { blockedTargetTooltip } from "../_shared/_logic/action-availability"
import type { TableRowAction } from "@kontextwerk/web-sdk/core/layout/table"
export default function buildActions(
  {
    row,
    order,
    stepId,
    caseId,
    contactOrderId,
    organizationId,
    targetContactPointUsable,
    canManageLetter,
    onApplyStepPatch,
    onContactOrderStepUpdated,
    onReloadOutreach,
  }: {
    row: ContactOrderPlanRow
    order: ContactOrder
    stepId: string
    caseId: string
    contactOrderId: string
    organizationId: string
    targetContactPointUsable: boolean
    canManageLetter: boolean
    onApplyStepPatch: (stepId: string, patch: ContactOrderStep) => void
    onContactOrderStepUpdated: (contactOrderId: string, step: ContactOrderStep) => void
    onReloadOutreach: () => Promise<void> | void
  },
  state: { markingDue: boolean },
): TableRowAction[] {
  const step =
    row.stepActionContext?.step ??
    row.step ??
    (row.source === "planned" ? (order.steps?.find((entry) => entry.id === row.id) ?? null) : null)
  const isManualLetter = (() => {
    if (!step || step.channel !== "LETTER") return false

    return step.execution_mode === "manual_letter_delivery"
  })()
  const stepStatus = String((step as ContactOrderStep | null)?.status || "").toUpperCase()
  const canMarkDueNow = Boolean(
    row.channel === "LETTER" &&
    canManageLetter &&
    organizationId &&
    stepId &&
    caseId &&
    contactOrderId &&
    isManualLetter &&
    !isDueManualLetterStep(step) &&
    stepStatus === "READY",
  )
  const tooltipContent = targetContactPointUsable
    ? "Setzt diesen Terminbrief jetzt fällig. Die weitere Bearbeitung erfolgt gemäß Ihren Terminbrief-Einstellungen."
    : blockedTargetTooltip
  const reloadOutreachAfterMarkingDue = async () => {
    try {
      await onReloadOutreach()
    } catch (err) {
      console.error("manual_letter_due_now_reload_failed", err)
      newNotification({
        class: "info",
        html: "Der Terminbrief ist fällig gesetzt. Die Anzeige konnte nicht vollständig aktualisiert werden.",
      })
    }
  }
  const markDueNow = async () => {
    if (!canMarkDueNow || !targetContactPointUsable || state.markingDue) return

    state.markingDue = true
    try {
      const updatedStep = await markManualLetterStepDueNow(caseId, contactOrderId, stepId)
      onContactOrderStepUpdated(order.id, updatedStep)
      onApplyStepPatch(stepId, updatedStep)
      newNotification({
        class: "success",
        html: "Terminbrief ist jetzt fällig. Die weitere Bearbeitung erfolgt gemäß Ihren Terminbrief-Einstellungen.",
      })
      await reloadOutreachAfterMarkingDue()
    } catch (err) {
      console.error("manual_letter_due_now_failed", err)
      newNotification({
        class: "error",
        html: "Terminbrief konnte nicht fällig gesetzt werden.",
      })
    } finally {
      state.markingDue = false
    }
  }
  const actions: TableRowAction[] = []

  if (canMarkDueNow) {
    actions.push({
      id: "action-0",
      label: "Terminbrief jetzt fällig setzen",
      onClick: () => markDueNow(),
      icon: mdiClockFast,
      disabled: state.markingDue || !targetContactPointUsable,
      tooltip: tooltipContent,
      testId: `controlAdmin.communicationPlan.letterAction.${stepId}`,
    })
  }

  return actions
}
